import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './NewsSettings.css';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const emptySection = () => ({ heading: '', image: '', paragraphs: [] });

const emptyNews = () => ({
  id: '',
  date: '',
  title: '',
  summary: '',
  heroImage: '',
  sections: [emptySection()],
});

function NewsSettings() {
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/news/section-cards`);
        if (Array.isArray(res.data?.cards)) {
          setCards(
            res.data.cards.map((c) => ({
              ...c,
              summary: c.summary ?? '',
              sections: Array.isArray(c.sections) ? c.sections : [emptySection()],
            }))
          );
        } else {
          setCards([]);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCards();
  }, []);

  const handleChange = (index, field, value) => {
    setCards((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const setSection = (cardIndex, sectionIndex, field, value) => {
    setCards((prev) => {
      const next = [...prev];
      const secs = [...(next[cardIndex].sections || [])];
      secs[sectionIndex] = { ...secs[sectionIndex], [field]: value };
      next[cardIndex] = { ...next[cardIndex], sections: secs };
      return next;
    });
  };

  const setSectionParagraphs = (cardIndex, sectionIndex, textareaValue) => {
    const paragraphs = textareaValue
      .split('\n')
      .map((p) => p.trim())
      .filter(Boolean);
    setSection(cardIndex, sectionIndex, 'paragraphs', paragraphs);
  };

  const getSectionParagraphsText = (cardIndex, sectionIndex) => {
    const card = cards[cardIndex];
    const sec = card?.sections?.[sectionIndex];
    return sec && Array.isArray(sec.paragraphs) ? sec.paragraphs.join('\n') : '';
  };

  const handleAddSection = (cardIndex) => {
    setCards((prev) => {
      const next = [...prev];
      const card = next[cardIndex];
      const sections = [...(card.sections || []), emptySection()];
      next[cardIndex] = { ...card, sections };
      return next;
    });
  };

  const handleDeleteSection = (cardIndex, sectionIndex) => {
    setCards((prev) => {
      const next = [...prev];
      const card = next[cardIndex];
      const sections = (card.sections || []).filter((_, i) => i !== sectionIndex);
      next[cardIndex] = { ...card, sections: sections.length ? sections : [emptySection()] };
      return next;
    });
  };

  const handleAdd = () => {
    setCards((prev) => [...prev, emptyNews()]);
  };

  const handleDelete = (index) => {
    setCards((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNewsImageUpload = async (index, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/news/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data?.url) {
        handleChange(index, 'heroImage', response.data.url);
        setMessage('Image uploaded successfully.');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setMessage('Unable to upload image. Please try again.');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const payload = cards.map((c) => ({
        ...c,
        sections: c.sections || [],
      }));
      const res = await axios.put(
        `${API_BASE_URL}/api/news/section-cards`,
        payload
      );
      if (Array.isArray(res.data?.cards)) {
        setCards(
          res.data.cards.map((c) => ({
            ...c,
            summary: c.summary ?? '',
            sections: Array.isArray(c.sections) ? c.sections : [emptySection()],
          }))
        );
      }
      setMessage('News section updated successfully.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setMessage('Unable to save news. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <p className="news-settings-loading">Loading news…</p>;
  }

  return (
    <div className="news-settings">
      <div className="news-settings-header">
        <div>
          <h2 className="news-settings-title">Home news section</h2>
          <p className="news-settings-subtitle">
            Control which news articles appear in the home page slider. IDs
            should match existing news articles.
          </p>
        </div>
        <div className="news-settings-actions">
          <button
            type="button"
            className="news-settings-secondary-btn"
            onClick={handleAdd}
          >
            + Add news card
          </button>
          <button
            type="button"
            className="news-settings-primary-btn"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="news-settings-list">
        {cards.map((card, index) => (
          <div key={card.id || index} className="news-settings-row">
            <div className="news-settings-col news-settings-col-main">
              <label className="news-settings-label">
                Article ID (must match /news/:id)
                <input
                  type="text"
                  className="news-settings-input"
                  value={card.id}
                  onChange={(e) =>
                    handleChange(index, 'id', e.target.value)
                  }
                  placeholder="1"
                />
              </label>

              <label className="news-settings-label">
                Title
                <input
                  type="text"
                  className="news-settings-input"
                  value={card.title}
                  onChange={(e) =>
                    handleChange(index, 'title', e.target.value)
                  }
                  placeholder="News title"
                />
              </label>

              <label className="news-settings-label">
                Date
                <input
                  type="text"
                  className="news-settings-input"
                  value={card.date}
                  onChange={(e) =>
                    handleChange(index, 'date', e.target.value)
                  }
                  placeholder="15 Jan 2026"
                />
              </label>

              <label className="news-settings-label">
                Summary (for list and detail page)
                <textarea
                  className="news-settings-textarea"
                  rows={2}
                  value={card.summary ?? ''}
                  onChange={(e) =>
                    handleChange(index, 'summary', e.target.value)
                  }
                  placeholder="Short summary for the news list and overview on detail page."
                />
              </label>
            </div>

            <div className="news-settings-col news-settings-col-meta">
              <label className="news-settings-label">
                Hero image URL
                <input
                  type="text"
                  className="news-settings-input"
                  value={card.heroImage}
                  onChange={(e) =>
                    handleChange(index, 'heroImage', e.target.value)
                  }
                  placeholder="https://example.com/news-image.jpg"
                />
              </label>

              <label className="news-settings-label">
                Or upload image
                <input
                  type="file"
                  accept="image/*"
                  className="news-settings-input"
                  onChange={(e) =>
                    handleNewsImageUpload(index, e.target.files?.[0] || null)
                  }
                />
              </label>

              <button
                type="button"
                className="news-settings-delete-btn"
                onClick={() => handleDelete(index)}
              >
                Remove
              </button>
            </div>

            <div className="news-settings-col news-settings-col-sections">
              <h4 className="news-settings-sections-title">Article sections (detail page)</h4>
              {(card.sections || []).map((sec, sIdx) => (
                <div key={sIdx} className="news-settings-section-block">
                  <div className="news-settings-section-actions">
                    <button
                      type="button"
                      className="news-settings-delete-btn"
                      onClick={() => handleDeleteSection(index, sIdx)}
                    >
                      Remove section
                    </button>
                  </div>
                  <label className="news-settings-label">
                    Section heading
                    <input
                      type="text"
                      className="news-settings-input"
                      value={sec.heading ?? ''}
                      onChange={(e) =>
                        setSection(index, sIdx, 'heading', e.target.value)
                      }
                    />
                  </label>
                  <label className="news-settings-label">
                    Section image URL
                    <input
                      type="text"
                      className="news-settings-input"
                      value={sec.image ?? ''}
                      onChange={(e) =>
                        setSection(index, sIdx, 'image', e.target.value)
                      }
                    />
                  </label>
                  <label className="news-settings-label">
                    Paragraphs (one per line)
                    <textarea
                      className="news-settings-textarea"
                      rows={3}
                      value={getSectionParagraphsText(index, sIdx)}
                      onChange={(e) =>
                        setSectionParagraphs(index, sIdx, e.target.value)
                      }
                    />
                  </label>
                </div>
              ))}
              <button
                type="button"
                className="news-settings-add-section-btn"
                onClick={() => handleAddSection(index)}
              >
                + Add section
              </button>
            </div>
          </div>
        ))}

        {cards.length === 0 && (
          <p className="news-settings-empty">
            No news cards configured yet. Click “Add news card” to create one.
          </p>
        )}
      </div>

      {message && <p className="news-settings-message">{message}</p>}
    </div>
  );
}

export default NewsSettings;

